import {
  UsersIcon,
  FolderOpenIcon,
  UserPlusIcon,
  FilePlusIcon,
  AlertCircleIcon,
  GaugeIcon,
  SlidersHorizontalIcon,
} from 'lucide-react'
import type { DashboardSummary } from '@/lib/api'

function CardFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md">
      {children}
    </div>
  )
}

function CardLabel({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="size-5 shrink-0 text-[var(--brand-mint)]" />
      <span className="text-sm text-muted-foreground">{children}</span>
    </div>
  )
}

function CardValue({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`text-2xl font-semibold tabular-nums ${className}`}>
      {children}
    </span>
  )
}

function CardExplanation({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs text-muted-foreground/80 leading-relaxed">
      {children}
    </p>
  )
}

export function ParticipationCard({ summary }: { summary: DashboardSummary }) {
  const rateColor =
    summary.participation_rate >= 50
      ? 'text-emerald-600 dark:text-emerald-400'
      : summary.participation_rate >= 25
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-muted-foreground'

  return (
    <CardFrame>
      <CardLabel icon={UsersIcon}>Participation rate</CardLabel>
      <div className="flex items-baseline gap-2">
        <CardValue className={rateColor}>
          {summary.participation_rate}%
        </CardValue>
        <span className="text-xs text-muted-foreground/70">
          {summary.employees_with_active_circles} of {summary.total_employees} employees
        </span>
      </div>
      {/* Mini progress bar */}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            summary.participation_rate >= 50
              ? 'bg-emerald-500'
              : summary.participation_rate >= 25
                ? 'bg-amber-500'
                : 'bg-muted-foreground/30'
          }`}
          style={{ width: `${summary.participation_rate}%` }}
        />
      </div>
      <CardExplanation>
        {summary.employees_with_active_circles === 0
          ? 'No employees are currently participating in Funding Circles. Encourage employees to create or join a Circle to get started.'
          : `${summary.employees_with_active_circles} out of ${summary.total_employees} employee${summary.total_employees !== 1 ? 's' : ''} ${
              summary.employees_with_active_circles === 1 ? 'is' : 'are'
            } actively participating in a Funding Circle${
              summary.employees_with_active_circles === 1 ? '' : 's'
            }. Participation rate shows how engaged your people are with the Funding Circle feature.`}
      </CardExplanation>
    </CardFrame>
  )
}

export function OpenCirclesCard({ summary }: { summary: DashboardSummary }) {
  return (
    <CardFrame>
      <CardLabel icon={FolderOpenIcon}>Open Funding Circles</CardLabel>
      <CardValue>{summary.open_circles_count}</CardValue>
      <CardExplanation>
        {summary.open_circles_count === 0
          ? 'There are currently no open Funding Circles. Employees can create a new Circle request to start saving or borrowing.'
          : (
            <>
              There {summary.open_circles_count === 1 ? 'is' : 'are'} currently{' '}
              <strong>{summary.open_circles_count}</strong>
              {' '} open Funding Circle{summary.open_circles_count === 1 ? '' : 's'} waiting for participants.
              {' '}Open Circles are requests that employees have created and are still accepting participants.
            </>
          )}
      </CardExplanation>
    </CardFrame>
  )
}

export function AvgParticipantsCard({ summary }: { summary: DashboardSummary }) {
  return (
    <CardFrame>
      <CardLabel icon={UserPlusIcon}>Average participants per Circle</CardLabel>
      <CardValue>
        {summary.open_circles_count === 0 ? '—' : summary.avg_participants_per_circle}
      </CardValue>
      <CardExplanation>
        {summary.open_circles_count === 0
          ? 'No open Circles yet. Average participants per Circle will appear once employees create Funding Circle requests and others join.'
          : (
            <>
              Across <strong>{summary.open_circles_count}</strong> open Circle{summary.open_circles_count === 1 ? '' : 's'},{' '}
              each has an average of <strong>{summary.avg_participants_per_circle}</strong> participant{summary.avg_participants_per_circle === 1 ? '' : 's'}.
              {' '}This shows how collaborative your Funding Circles are — more participants means shared funding across more people.
            </>
          )}
      </CardExplanation>
    </CardFrame>
  )
}

export function NewRequestsCard({ summary }: { summary: DashboardSummary }) {
  return (
    <CardFrame>
      <CardLabel icon={FilePlusIcon}>New requests this month</CardLabel>
      <CardValue>{summary.new_requests_this_month}</CardValue>
      <CardExplanation>
        {summary.new_requests_this_month === 0
          ? 'No new Funding Circle requests have been created this month. This could mean employees are not actively seeking salary advances right now.'
          : (
            <>
              <strong>{summary.new_requests_this_month}</strong> new Funding Circle request{summary.new_requests_this_month === 1 ? '' : 's'} have been created this month.
              {' '}This shows current demand for Funding Circles — a rising trend may indicate growing financial needs among your workforce.
            </>
          )}
      </CardExplanation>
    </CardFrame>
  )
}

export function NearingCapacityCard({ summary }: { summary: DashboardSummary }) {
  return (
    <CardFrame>
      <div className="flex items-center justify-between">
        <CardLabel icon={AlertCircleIcon}>Circles nearing capacity</CardLabel>
        {summary.circles_nearing_capacity > 0 && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-600 dark:bg-amber-950/30 dark:text-amber-400">
            <AlertCircleIcon className="size-3" />
            Needs attention
          </span>
        )}
      </div>
      <CardValue className={summary.circles_nearing_capacity > 0 ? 'text-amber-600 dark:text-amber-400' : ''}>
        {summary.circles_nearing_capacity}
      </CardValue>
      <CardExplanation>
        {summary.circles_nearing_capacity === 0
          ? 'No open Circles are nearing their maximum participant limit. All active Circles have sufficient capacity for new participants.'
          : (
            <>
              <strong>{summary.circles_nearing_capacity}</strong> open Circle{summary.circles_nearing_capacity === 1 ? '' : 's'}{' '}
              {summary.circles_nearing_capacity === 1 ? 'is' : 'are'} at 80% or more of{' '}
              {summary.circles_nearing_capacity === 1 ? 'its' : 'their'} maximum participant capacity.
              {' '}Consider funding these Circles soon to keep the cycle running smoothly.
            </>
          )}
      </CardExplanation>
    </CardFrame>
  )
}

export function CapacityCard({ summary }: { summary: DashboardSummary }) {
  const pressureColor =
    summary.total_spots === 0
      ? 'text-muted-foreground'
      : summary.capacity_pressure >= 80
        ? 'text-red-600 dark:text-red-400'
        : summary.capacity_pressure >= 50
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-emerald-600 dark:text-emerald-400'

  return (
    <CardFrame>
      <CardLabel icon={GaugeIcon}>Capacity pressure</CardLabel>
      <div className="flex items-baseline gap-2">
        <CardValue className={pressureColor}>
          {summary.total_spots === 0 ? '—' : `${summary.capacity_pressure}%`}
        </CardValue>
        {summary.total_spots > 0 && (
          <span className="text-xs text-muted-foreground/70">
            {summary.spots_filled} of {summary.total_spots} spots filled
          </span>
        )}
      </div>
      {/* Mini progress bar */}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            summary.total_spots === 0
              ? 'bg-muted-foreground/20'
              : summary.capacity_pressure >= 80
                ? 'bg-red-500'
                : summary.capacity_pressure >= 50
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'
          }`}
          style={{
            width: summary.total_spots === 0 ? '0%' : `${summary.capacity_pressure}%`,
          }}
        />
      </div>
      <CardExplanation>
        {summary.total_spots === 0
          ? 'No open Circles with available spots yet. Capacity pressure will appear once employees create open Funding Circles with participant limits.'
          : (
            <>
              <strong>{summary.spots_filled}</strong> of{' '}
              <strong>{summary.total_spots}</strong> participant spot{summary.total_spots !== 1 ? 's' : ''} across all open Circles{' '}
              {summary.capacity_pressure >= 80
                ? 'are filled — your Circles are nearly at full capacity.'
                : summary.capacity_pressure >= 50
                  ? 'are filled — moderate capacity pressure.'
                  : 'are filled — plenty of room for new participants.'
              }
            </>
          )}
      </CardExplanation>
    </CardFrame>
  )
}

export function OverrideCard({ summary }: { summary: DashboardSummary }) {
  const overridesCount = summary.employees_with_overrides
  const totalEmp = summary.total_employees

  return (
    <CardFrame>
      <div className="flex items-center justify-between">
        <CardLabel icon={SlidersHorizontalIcon}>Custom profiles</CardLabel>
        {overridesCount > 0 && totalEmp > 0 && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600 dark:bg-blue-950/30 dark:text-blue-400">
            {Math.round((overridesCount / totalEmp) * 100)}% of employees
          </span>
        )}
      </div>
      <CardValue>
        {overridesCount}
      </CardValue>
      <CardExplanation>
        {overridesCount === 0
          ? 'No employees have custom Funding Circle profile overrides. Everyone is using the company default settings.'
          : (
            <>
              <strong>{overridesCount}</strong> employee{overridesCount === 1 ? '' : 's'}{' '}
              {overridesCount === 1 ? 'has' : 'have'} custom Circle profile override{overridesCount === 1 ? '' : 's'}.
              {' '}These employees have individual limits or settings that differ from the company defaults — worth reviewing if you see unusual Circle activity.
            </>
          )}
      </CardExplanation>
    </CardFrame>
  )
}
